<template>
  <div class="frq">
    <div class="frq-head">
      <div class="frq-avatar" :style="avatarStyle">{{ initials }}</div>
      <div class="frq-meta">
        <div class="frq-author">{{ authorName }}</div>
        <div class="frq-sub">
          <span v-if="original.groupName">{{ original.groupName }} · </span>{{ original.timeAgo || '' }}
        </div>
      </div>
      <span v-if="badgeLabel" class="frq-tag" :class="'frq-tag--' + (original.tagColor || badgeColor)">
        {{ badgeLabel }}
      </span>
    </div>

    <div v-if="original.headline" class="frq-headline">{{ original.headline }}</div>
    <div v-if="original.text" class="frq-text">{{ original.text }}</div>

    <FeedMediaGrid
      v-if="original.media && original.media.length"
      :urls="original.media.slice(0, 4)"
      @open="openMedia"
    />

    <div v-if="isPoll && original.poll" class="frq-poll">
      <div class="frq-poll-title">{{ original.poll.title }}</div>
      <div v-for="opt in original.poll.options || []" :key="opt.id" class="frq-poll-option">
        <div class="frq-poll-bar" :style="{ width: optionPct(opt) + '%' }"></div>
        <span class="frq-poll-label">{{ opt.label }}</span>
        <span class="frq-poll-pct">{{ optionPct(opt) }}%</span>
      </div>
      <div class="frq-poll-footer">{{ totalVotes }} {{$t('common.vote')}}{{ totalVotes > 1 ? 's' : '' }}</div>
    </div>

    <div v-if="isSponsored" class="frq-sponsored">
      <div class="frq-sponsor">{{ original.sponsorName }}</div>
      <div v-if="original.description" class="frq-desc">{{ original.description }}</div>
      <div v-if="original.stats" class="frq-stats">
        <span v-for="s in original.stats" :key="s.label" class="frq-stat">
          <strong>{{ s.value }}</strong> {{ s.label }}
        </span>
      </div>
    </div>

    <div v-if="original.campaignAttachment" class="frq-campaign" :style="{ borderColor: campaignColor }">
      <div class="frq-campaign-icon" :style="{ background: campaignIconBg }">
        <f7-icon :f7="campaignIcon" size="14" :style="{ color: campaignColor }" />
      </div>
      <div class="frq-campaign-meta">
        <div class="frq-campaign-name">{{ original.campaignAttachment.name }}</div>
        <div class="frq-campaign-sub">{{ original.campaignAttachment.sub }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { f7 } from 'framework7-vue'
import FeedMediaGrid from './FeedMediaGrid.vue'
import i18n from '../../js/i18n.js'
const props = defineProps({
  item: { type: Object, required: true },
})

const original = computed(() => {
  let current = props.item || {}
  let guard = 0
  while (current.originalPost && guard < 5) {
    current = current.originalPost
    guard++
  }
  return current
})

const isPoll = computed(() => original.value.type === 'POLL' || original.value.type === 'SPONSORED_POLL')
const isSponsored = computed(() => original.value.type === 'SPONSORED_POST' || original.value.type === 'SPONSORED_POLL')

const authorName = computed(() => {
  const o = original.value
  return o.authorName || o.sponsorName || o.author?.name || ''
})

const initials = computed(() => {
  const name = authorName.value
  return name.split(' ').map((w) => w[0]).join('').substring(0, 2).toUpperCase()
})

const avatarStyle = computed(() => {
  const colors = ['#1A6B42', '#3B4FCA', '#C9A84C', '#C0440A', '#6B46C1']
  const hash = authorName.value.split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  const bg = colors[hash % colors.length]
  return { background: bg, color: '#fff' }
})

const badgeLabel = computed(() => {
  const o = original.value
  if (o.tag) return o.tag
  if (isPoll.value) return i18n.global.t('common.poll')
  if (isSponsored.value) return i18n.global.t('common.sponsored')
  return ''
})

const badgeColor = computed(() => {
  if (isPoll.value) return 'blue'
  if (isSponsored.value) return 'gold'
  return 'green'
})

const totalVotes = computed(() => original.value.poll?.totalVotes || 0)

function optionPct(opt) {
  const total = totalVotes.value || 1
  return Math.round((opt.votes / total) * 100)
}

const campaignColor = computed(() => {
  const type = original.value.campaignAttachment?.type
  const map = { FUND_COLLECT: '#97C459', GROUP_SAVINGS: '#BA7517', TONTINE: '#3B4FCA', GROUP_BUY: '#3B4FCA' }
  return map[type] || '#1A6B42'
})

const campaignIcon = computed(() => {
  const type = original.value.campaignAttachment?.type
  const map = { FUND_COLLECT: 'gift_fill', GROUP_SAVINGS: 'device_floppy_fill', TONTINE: 'arrow_2_circlepath', GROUP_BUY: 'bag_fill' }
  return map[type] || 'star_fill'
})

const campaignIconBg = computed(() => {
  const type = original.value.campaignAttachment?.type
  const map = { FUND_COLLECT: '#EAF3DE', GROUP_SAVINGS: '#FAEEDA', TONTINE: '#EBF0FC', GROUP_BUY: '#EBF0FC' }
  return map[type] || '#EAF2EB'
})

function openMedia(index) {
  if (original.value.media && original.value.media.length > 0) {
    f7.photoBrowser.open({
      photos: original.value.media.map((url) => ({ url })),
      initialSlide: index,
    })
  }
}
</script>

<style scoped>
.frq {
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 10px;
  padding: 9px 10px;
  background: var(--f7-list-bg-color, #f7f7f7);
  cursor: pointer;
}
.frq-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}
.frq-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  flex-shrink: 0;
}
.frq-meta { flex: 1; min-width: 0; }
.frq-author { font-size: 11px; font-weight: 500; }
.frq-sub { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); }
.frq-tag {
  font-size: 9px;
  font-weight: 500;
  padding: 1px 7px;
  border-radius: 8px;
  flex-shrink: 0;
}
.frq-tag--green { background: #EAF3DE; color: #27500A; }
.frq-tag--blue { background: #EBF0FC; color: #0C447C; }
.frq-tag--gold { background: #FDF6E3; color: #92400E; }
.frq-tag--purple { background: #F5F0FF; color: #6B46C1; }
.frq-tag--leaf { background: #EAF2EB; color: #3B6D11; }
.frq-headline {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 3px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.frq-text {
  font-size: 11px;
  line-height: 1.5;
  color: var(--f7-text-color, #000);
  margin-bottom: 6px;
  white-space: pre-wrap;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.frq-poll { margin-top: 4px; }
.frq-poll-title {
  font-size: 11px;
  font-weight: 500;
  margin-bottom: 4px;
}
.frq-poll-option {
  position: relative;
  border-radius: 5px;
  padding: 4px 8px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  overflow: hidden;
  margin-bottom: 3px;
  background: var(--f7-page-bg-color, #fff);
}
.frq-poll-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #EAF2EB;
}
.frq-poll-label {
  position: relative;
  font-size: 10px;
  display: inline-block;
}
.frq-poll-pct {
  position: relative;
  float: right;
  font-size: 10px;
  font-weight: 600;
}
.frq-poll-footer {
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.frq-sponsored { margin-top: 4px; }
.frq-sponsor { font-size: 11px; font-weight: 500; }
.frq-desc { font-size: 11px; line-height: 1.45; margin-top: 2px; }
.frq-stats {
  display: flex;
  gap: 10px;
  margin-top: 5px;
  flex-wrap: wrap;
}
.frq-stat {
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.frq-stat strong { color: var(--f7-text-color, #000); }
.frq-campaign {
  margin-top: 6px;
  border-radius: 8px;
  padding: 7px 9px;
  border: 0.5px solid;
  background: var(--f7-page-bg-color, #fff);
  display: flex;
  align-items: center;
  gap: 8px;
}
.frq-campaign-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.frq-campaign-meta { min-width: 0; }
.frq-campaign-name { font-size: 10px; font-weight: 500; }
.frq-campaign-sub {
  font-size: 9px;
  color: var(--f7-on-surface-variant, #8e8e93);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
